import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../apiClientContext";
import {
    fetchQueuePriorityPolicy,
    fetchTaskTypeWeights,
    updateQueuePriorityPolicy,
    updateTaskTypeWeights,
} from "./api";
import type {
    UpdateQueuePriorityPolicyInput,
    UpdateTaskTypeWeightsInput,
} from "./api";
import type {
    QueuePriorityPolicyResponse,
    TaskTypeWeightsResponse,
} from "./types";

const nextRankingKeys = {
    all: ["next-ranking"] as const,
    queuePriorityPolicy: ["next-ranking", "queue-priority-policy"] as const,
    taskTypeWeights: ["next-ranking", "task-type-weights"] as const,
};

export function useQueuePriorityPolicy() {
    const client = useApiClient();
    return useQuery({
        queryKey: nextRankingKeys.queuePriorityPolicy,
        queryFn: () => fetchQueuePriorityPolicy(client),
        // Policy is governed config that changes deliberately and rarely;
        // a generous window keeps the editor calm without going stale on a
        // genuine edit (the mutation seeds the cache with the server's
        // authoritative post-write copy).
        staleTime: 60_000,
    });
}

export function useUpdateQueuePriorityPolicy() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: UpdateQueuePriorityPolicyInput) =>
            updateQueuePriorityPolicy(client, input),
        onSuccess: (policy: QueuePriorityPolicyResponse) => {
            queryClient.setQueryData(nextRankingKeys.queuePriorityPolicy, policy);
        },
    });
}

export function useTaskTypeWeights() {
    const client = useApiClient();
    return useQuery({
        queryKey: nextRankingKeys.taskTypeWeights,
        queryFn: () => fetchTaskTypeWeights(client),
        staleTime: 60_000,
    });
}

export function useUpdateTaskTypeWeights() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: UpdateTaskTypeWeightsInput) =>
            updateTaskTypeWeights(client, input),
        onSuccess: (weights: TaskTypeWeightsResponse) => {
            queryClient.setQueryData(nextRankingKeys.taskTypeWeights, weights);
        },
    });
}
